import React, { useState, useRef, useEffect } from "react";
import { Sparkles, X, Send } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { useI18n } from "@/lib/i18n";

export default function AIConsultant() {
  const { profile } = useProfile();
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const history = msgs.map((m) => ({ role: m.role, text: m.text }));
    setMsgs((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/consult", { message: text, profile, history });
      setMsgs((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (err) {
      setMsgs((m) => [...m, { role: "assistant", text: "⚠️ " + (formatError(err.response?.data?.detail) || "Servizio non disponibile.") }]);
    } finally { setLoading(false); }
  };

  const greeting = lang === "it"
    ? "Ciao! Sono il tuo Consulente Velyrea. Chiedimi consigli su trucco, colori, capelli o outfit ✨"
    : "Hi! I'm your Velyrea Consultant. Ask me about make-up, colors, hair or outfits ✨";

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="ai-fab"
        className="fixed bottom-5 right-5 z-[55] w-14 h-14 rounded-full rose-gold-bg text-white shadow-xl flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="Consulente AI">
        <Sparkles size={24} />
      </button>

      {open && (
        <div className="fixed bottom-5 right-5 z-[70] w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-white rounded-2xl shadow-2xl border border-olive/20 flex flex-col overflow-hidden" data-testid="ai-panel">
          <div className="rose-gold-bg text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={18} /><span className="font-serif text-lg">Consulente Velyrea</span>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ai-close"><X size={20} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-surface" data-testid="ai-messages">
            <div className="text-sm text-charcoal/70 bg-white rounded-xl p-3 border border-olive/10">{greeting}</div>
            {msgs.map((m, i) => (
              <div key={i} className={`text-sm rounded-xl p-3 max-w-[85%] whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-charcoal text-white" : "bg-white border border-olive/10 text-charcoal/85"}`}>
                {m.text}
              </div>
            ))}
            {loading && <div className="text-sm text-charcoal/50 bg-white rounded-xl p-3 border border-olive/10 w-24 animate-pulse">…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-olive/15 flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={lang === "it" ? "Scrivi la tua domanda..." : "Type your question..."} data-testid="ai-input"
              className="flex-1 border border-olive/20 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-olive" />
            <button onClick={send} disabled={loading} data-testid="ai-send" className="w-10 h-10 rounded-full bg-olive hover:bg-olive-dark text-white flex items-center justify-center transition-colors disabled:opacity-50">
              <Send size={17} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
